import React, { useState } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CorrelationAnalysis = () => {
  const [xAxis, setXAxis] = useState('temperature');
  const [yAxis, setYAxis] = useState('effectiveness');
  const [timeFilter, setTimeFilter] = useState('30-days');

  const correlationData = [
    {
      id: 1,
      temperature: 72,
      humidity: 65,
      windSpeed: 8,
      rainfall: 0.2,
      effectiveness: 94,
      cost: 42,
      pestPressure: 35,
      applicationTiming: 'Morning',
      weatherCondition: 'Optimal'
    },
    {
      id: 2,
      temperature: 68,
      humidity: 78,
      windSpeed: 12,
      rainfall: 1.5,
      effectiveness: 76,
      cost: 58,
      pestPressure: 62,
      applicationTiming: 'Afternoon',
      weatherCondition: 'Rainy'
    },
    {
      id: 3,
      temperature: 75,
      humidity: 58,
      windSpeed: 6,
      rainfall: 0,
      effectiveness: 91,
      cost: 38,
      pestPressure: 28,
      applicationTiming: 'Morning',
      weatherCondition: 'Optimal'
    },
    {
      id: 4,
      temperature: 79,
      humidity: 45,
      windSpeed: 15,
      rainfall: 0,
      effectiveness: 82,
      cost: 45,
      pestPressure: 48,
      applicationTiming: 'Evening',
      weatherCondition: 'Windy'
    },
    {
      id: 5,
      temperature: 71,
      humidity: 72,
      windSpeed: 9,
      rainfall: 0.8,
      effectiveness: 88,
      cost: 41,
      pestPressure: 42,
      applicationTiming: 'Morning',
      weatherCondition: 'Cloudy'
    },
    {
      id: 6,
      temperature: 77,
      humidity: 52,
      windSpeed: 7,
      rainfall: 0.1,
      effectiveness: 93,
      cost: 39,
      pestPressure: 31,
      applicationTiming: 'Morning',
      weatherCondition: 'Optimal'
    },
    {
      id: 7,
      temperature: 65,
      humidity: 85,
      windSpeed: 14,
      rainfall: 2.1,
      effectiveness: 69,
      cost: 62,
      pestPressure: 71,
      applicationTiming: 'Afternoon',
      weatherCondition: 'Stormy'
    },
    {
      id: 8,
      temperature: 74,
      humidity: 61,
      windSpeed: 5,
      rainfall: 0,
      effectiveness: 96,
      cost: 36,
      pestPressure: 25,
      applicationTiming: 'Morning',
      weatherCondition: 'Optimal'
    }
  ];

  const axisOptions = [
    { value: 'temperature', label: 'Temperature (°F)' },
    { value: 'humidity', label: 'Humidity (%)' },
    { value: 'windSpeed', label: 'Wind Speed (mph)' },
    { value: 'rainfall', label: 'Rainfall (inches)' },
    { value: 'effectiveness', label: 'Treatment Effectiveness (%)' },
    { value: 'cost', label: 'Cost per Acre ($)' },
    { value: 'pestPressure', label: 'Pest Pressure Index' }
  ];

  const timeFilterOptions = [
    { value: '7-days', label: 'Last 7 Days' },
    { value: '30-days', label: 'Last 30 Days' },
    { value: '90-days', label: 'Last 90 Days' },
    { value: 'season', label: 'Current Season' }
  ];

  const getPointColor = (point) => {
    if (point?.weatherCondition === 'Optimal') return '#2D5A27';
    if (point?.weatherCondition === 'Cloudy') return '#F4A460';
    if (point?.weatherCondition === 'Windy') return '#8B4513';
    if (point?.weatherCondition === 'Rainy') return '#6B7280';
    return '#DC2626';
  };

  const calculateCorrelation = () => {
    const xValues = correlationData?.map(d => d?.[xAxis]);
    const yValues = correlationData?.map(d => d?.[yAxis]);
    
    const n = xValues?.length;
    const sumX = xValues?.reduce((a, b) => a + b, 0);
    const sumY = yValues?.reduce((a, b) => a + b, 0);
    const sumXY = xValues?.reduce((sum, x, i) => sum + x * yValues?.[i], 0);
    const sumX2 = xValues?.reduce((sum, x) => sum + x * x, 0);
    const sumY2 = yValues?.reduce((sum, y) => sum + y * y, 0);
    
    const correlation = (n * sumXY - sumX * sumY) / 
      Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    return isNaN(correlation) ? 0 : correlation;
  };

  const correlation = calculateCorrelation();
  const correlationStrength = Math.abs(correlation);
  const correlationDirection = correlation > 0 ? 'Positive' : 'Negative';

  const getCorrelationColor = () => {
    if (correlationStrength >= 0.7) return 'text-success';
    if (correlationStrength >= 0.4) return 'text-warning';
    return 'text-error';
  };

  const getCorrelationDescription = () => {
    if (correlationStrength >= 0.7) return 'Strong';
    if (correlationStrength >= 0.4) return 'Moderate';
    return 'Weak';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-4 agricultural-shadow">
          <div className="font-medium text-foreground mb-2">
            Treatment Application
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {axisOptions?.find(opt => opt?.value === xAxis)?.label}:
              </span>
              <span className="font-medium text-foreground">{data?.[xAxis]}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {axisOptions?.find(opt => opt?.value === yAxis)?.label}:
              </span>
              <span className="font-medium text-foreground">{data?.[yAxis]}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Weather:</span>
              <span className="font-medium text-foreground">{data?.weatherCondition}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Timing:</span>
              <span className="font-medium text-foreground">{data?.applicationTiming}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Correlation Analysis
            </h2>
            <p className="text-sm text-muted-foreground">
              Explore relationships between weather conditions and treatment effectiveness
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              options={timeFilterOptions}
              value={timeFilter}
              onChange={setTimeFilter}
              placeholder="Time Range"
              className="w-40"
            />
            <Button variant="outline" size="sm" iconName="Download">
              Export Analysis
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Controls */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                X-Axis Variable
              </label>
              <Select
                options={axisOptions}
                value={xAxis}
                onChange={setXAxis}
                placeholder="Select X-Axis"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Y-Axis Variable
              </label>
              <Select
                options={axisOptions}
                value={yAxis}
                onChange={setYAxis}
                placeholder="Select Y-Axis"
              />
            </div>

            {/* Correlation Statistics */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-foreground">Correlation Statistics</h4>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Coefficient:</span>
                  <span className={`font-bold ${getCorrelationColor()}`}>
                    {correlation?.toFixed(3)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Strength:</span>
                  <span className="font-medium text-foreground">
                    {getCorrelationDescription()}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Direction:</span>
                  <span className="font-medium text-foreground">
                    {correlationDirection}
                  </span>
                </div>
              </div>
              
              <div className="pt-3 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">
                  Interpretation:
                </div>
                <div className="text-xs text-foreground">
                  {correlationStrength >= 0.7 
                    ? `Strong ${correlationDirection?.toLowerCase()} relationship between variables`
                    : correlationStrength >= 0.4
                    ? `Moderate ${correlationDirection?.toLowerCase()} relationship between variables`
                    : 'Weak or no linear relationship between variables'
                  }
                </div>
              </div>
            </div>

            {/* Weather Legend */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Weather Conditions</h4>
              <div className="space-y-2">
                {['Optimal', 'Cloudy', 'Windy', 'Rainy', 'Stormy']?.map((condition) => (
                  <div key={condition} className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getPointColor({ weatherCondition: condition }) }}
                    />
                    <span className="text-xs text-muted-foreground">{condition}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Scatter Plot */}
          <div className="xl:col-span-3">
            <div className="h-96 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart data={correlationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey={xAxis}
                    stroke="#6B7280"
                    label={{ 
                      value: axisOptions?.find(opt => opt?.value === xAxis)?.label, 
                      position: 'insideBottom', 
                      offset: -10 
                    }}
                  />
                  <YAxis 
                    dataKey={yAxis}
                    stroke="#6B7280"
                    label={{ 
                      value: axisOptions?.find(opt => opt?.value === yAxis)?.label, 
                      angle: -90, 
                      position: 'insideLeft' 
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Scatter dataKey={yAxis}>
                    {correlationData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getPointColor(entry)} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            {/* Insights */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <Icon name="TrendingUp" size={24} className="mx-auto mb-2 text-success" />
                <div className="text-lg font-bold text-foreground">8</div>
                <div className="text-xs text-muted-foreground">Data Points</div>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <Icon name="Target" size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-lg font-bold text-foreground">
                  {Math.round(correlationStrength * 100)}%
                </div>
                <div className="text-xs text-muted-foreground">Correlation Strength</div>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <Icon name="Calendar" size={24} className="mx-auto mb-2 text-warning" />
                <div className="text-lg font-bold text-foreground">30</div>
                <div className="text-xs text-muted-foreground">Days Analyzed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrelationAnalysis;